import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Table, Button, Modal, Form, Input, Select, DatePicker, InputNumber,
  Tabs, Spin, Tag, Popconfirm, message, Checkbox, Radio, Space, Divider,
} from "antd";
import { ArrowLeft, Plus, Briefcase } from "lucide-react";
import dayjs from "dayjs";
import {
  useJobList, useJobCreate, useJobUpdate, useJobDelete,
  useJobClients, useJobUsers,
  useJobCFDefs, useJobCFValues, useSaveJobCFValues,
  useJobFormTemplates, useJobFormTemplateDetail, useSaveFormResponses,
  useJobTasks, useTaskCreate, useTaskUpdate, useTaskDelete,
  useChecklistTemplates, useChecklistResponses, useSaveChecklistResponse,
} from "../../../api-hooks/jobs";

const STATUS_OPTS = [
  { value: "draft", label: "Draft" },
  { value: "scheduled", label: "Scheduled" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const PRIORITY_OPTS = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

const STATUS_TAG: Record<string, string> = {
  draft: "default", scheduled: "orange", in_progress: "blue",
  completed: "green", cancelled: "red",
};
const PRIORITY_TAG: Record<string, string> = {
  low: "default", normal: "blue", high: "orange", urgent: "red",
};

// ── Dynamic form field renderer ────────────────────────────────────────────

function renderFormField(field: any, form: any) {
  const name = ["form_fields", String(field.id)];
  const label = field.field_label + (field.is_required ? " *" : "");

  switch (field.field_type) {
    case "checkbox":
      return (
        <Form.Item key={field.id} name={name} valuePropName="checked" label={label}>
          <Checkbox />
        </Form.Item>
      );
    case "radio": {
      let opts: any[] = [];
      try { opts = typeof field.options === "string" ? JSON.parse(field.options) : field.options || []; } catch {}
      return (
        <Form.Item key={field.id} name={name} label={label}>
          <Radio.Group>
            {opts.map((o: any) => (
              <Radio key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</Radio>
            ))}
          </Radio.Group>
        </Form.Item>
      );
    }
    case "select": {
      let opts: any[] = [];
      try { opts = typeof field.options === "string" ? JSON.parse(field.options) : field.options || []; } catch {}
      return (
        <Form.Item key={field.id} name={name} label={label}>
          <Select options={opts.map((o: any) => ({ value: o.value ?? o, label: o.label ?? o }))} placeholder={field.placeholder} />
        </Form.Item>
      );
    }
    case "textarea":
      return (
        <Form.Item key={field.id} name={name} label={label}>
          <Input.TextArea rows={3} placeholder={field.placeholder || field.help_text} />
        </Form.Item>
      );
    case "number":
      return (
        <Form.Item key={field.id} name={name} label={label}>
          <InputNumber className="w-full" placeholder={field.placeholder} />
        </Form.Item>
      );
    case "date":
      return (
        <Form.Item key={field.id} name={name} label={label}>
          <DatePicker className="w-full" />
        </Form.Item>
      );
    default:
      return (
        <Form.Item key={field.id} name={name} label={label}>
          <Input placeholder={field.placeholder || field.help_text} />
        </Form.Item>
      );
  }
}

// ── Page ──────────────────────────────────────────────────────────────────

export default function JobsPage() {
  const { companyId: cParam } = useParams<{ companyId: string }>();
  const cid = Number(cParam);
  const navigate = useNavigate();
  const [msgApi, ctx] = message.useMessage();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);

  const params = { search: search || undefined, status: statusFilter || undefined, page, limit: 20 };
  const { data: jobsRes, isLoading } = useJobList(cid, params);
  const jobs = jobsRes?.data ?? [];
  const total = jobsRes?.total ?? 0;

  const { data: clients = [] } = useJobClients(cid);
  const { data: users = [] } = useJobUsers(cid);
  const { data: cfDefs = [] } = useJobCFDefs(cid);
  const { data: formTemplates = [] } = useJobFormTemplates(cid);
  const { data: checklistTemplates = [] } = useChecklistTemplates(cid);

  // ── Modal state ────────────────────────────────────────────────────────────
  const [modal, setModal] = useState<{ open: boolean; job: any | null; step: "done" | null }>({ open: false, job: null, step: null });
  const [activeTab, setActiveTab] = useState("details");
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [createdJobId, setCreatedJobId] = useState<number | null>(null);

  // Task state
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<any | null>(null);
  const [taskForm] = Form.useForm();

  // Checklist state
  const [selectedChecklistId, setSelectedChecklistId] = useState<number | null>(null);

  const [detailsForm] = Form.useForm();
  const [formDataForm] = Form.useForm();

  const { data: cfValues = [] } = useJobCFValues(cid, modal.job?.id ?? 0);
  const { data: templateDetail } = useJobFormTemplateDetail(cid, selectedTemplateId ?? 0);
  const { data: tasks = [], refetch: refetchTasks } = useJobTasks(cid, createdJobId ?? 0);
  const { data: checklistResponses = [], refetch: refetchChecklist } = useChecklistResponses(cid, createdJobId ?? 0);

  const createJob = useJobCreate(cid, {
    onSuccess: (r) => {
      const job = r.data?.data;
      setCreatedJobId(job?.id ?? null);
      msgApi.success("Job created.");
    },
    onError: (e: any) => msgApi.error(e?.message || "Failed to create job."),
  });

  const updateJob = useJobUpdate(cid, {
    onSuccess: () => {
      msgApi.success("Job updated.");
      closeModal();
    },
    onError: (e: any) => msgApi.error(e?.message || "Failed to update job."),
  });

  const deleteJob = useJobDelete(cid, {
    onSuccess: () => msgApi.success("Job deleted."),
    onError: (e: any) => msgApi.error(e?.message || "Failed to delete job."),
  });

  const saveCFValues = useSaveJobCFValues(cid, {
    onSuccess: () => msgApi.success("Custom fields saved."),
    onError: (e: any) => msgApi.error(e?.message || "Failed to save custom fields."),
  });

  const saveFormResp = useSaveFormResponses(cid, {
    onSuccess: () => {
      msgApi.success("Form responses saved.");
      closeModal();
    },
    onError: (e: any) => msgApi.error(e?.message || "Failed to save form responses."),
  });

  const createTask = useTaskCreate(cid, createdJobId, {
    onSuccess: () => { msgApi.success("Task created."); setTaskModalOpen(false); taskForm.resetFields(); refetchTasks(); },
    onError: (e: any) => msgApi.error(e?.message || "Failed to create task."),
  });
  const updateTask = useTaskUpdate(cid, createdJobId, {
    onSuccess: () => { msgApi.success("Task updated."); setTaskModalOpen(false); taskForm.resetFields(); refetchTasks(); },
    onError: (e: any) => msgApi.error(e?.message || "Failed to update task."),
  });
  const deleteTask = useTaskDelete(cid, createdJobId, {
    onSuccess: () => { msgApi.success("Task deleted."); refetchTasks(); },
    onError: (e: any) => msgApi.error(e?.message || "Failed to delete task."),
  });
  const saveChecklistResp = useSaveChecklistResponse(cid, createdJobId, {
    onSuccess: () => { msgApi.success("Response saved."); refetchChecklist(); },
    onError: (e: any) => msgApi.error(e?.message || "Failed to save response."),
  });

  function openCreate() {
    setModal({ open: true, job: null, step: null });
    setActiveTab("details");
    setSelectedTemplateId(null);
    setCreatedJobId(null);
    setSelectedChecklistId(null);
    detailsForm.resetFields();
    formDataForm.resetFields();
  }

  function openEdit(job: any) {
    setModal({ open: true, job, step: null });
    setActiveTab("details");
    setSelectedTemplateId(null);
    setCreatedJobId(job.id);
    detailsForm.setFieldsValue({
      title: job.title,
      status: job.status,
      priority: job.priority,
      client_id: job.client_id,
      assigned_user_id: job.assigned_user_id,
      scheduled_start: job.scheduled_start ? dayjs(job.scheduled_start) : null,
      scheduled_end: job.scheduled_end ? dayjs(job.scheduled_end) : null,
      estimated_value: job.estimated_value,
      po_number: job.po_number,
      description: job.description,
      notes: job.notes,
    });
    // Pre-fill custom fields
    if (cfValues.length > 0) {
      const cfInitial: Record<string, any> = {};
      cfValues.forEach((v: any) => {
        cfInitial[`cf_${v.definition_id}`] = v.value_text ?? v.value_number ?? v.value_bool;
      });
      detailsForm.setFieldsValue(cfInitial);
    }
    formDataForm.resetFields();
  }

  function closeModal() {
    setModal({ open: false, job: null, step: null });
    setCreatedJobId(null);
    setSelectedTemplateId(null);
    setSelectedChecklistId(null);
    detailsForm.resetFields();
    formDataForm.resetFields();
  }

  async function handleSaveDetails() {
    try {
      const vals = await detailsForm.validateFields();
      const payload: any = {
        title: vals.title,
        status: vals.status,
        priority: vals.priority,
        client_id: vals.client_id,
        assigned_user_id: vals.assigned_user_id,
        scheduled_start: vals.scheduled_start?.toISOString(),
        scheduled_end: vals.scheduled_end?.toISOString(),
        estimated_value: vals.estimated_value,
        po_number: vals.po_number,
        description: vals.description,
        notes: vals.notes,
      };

      if (modal.job) {
        updateJob.mutate({ id: modal.job.id, ...payload });
      } else {
        createJob.mutate(payload);
      }
    } catch {}
  }

  async function handleSaveCF() {
    if (!createdJobId) return msgApi.warning("Save job details first.");
    const vals = detailsForm.getFieldsValue();
    const values = cfDefs.map((def: any) => ({
      definition_id: def.id,
      ...(def.field_type === "boolean"
        ? { value_bool: vals[`cf_${def.id}`] ?? false }
        : def.field_type === "number"
        ? { value_number: vals[`cf_${def.id}`] ?? null }
        : { value_text: vals[`cf_${def.id}`] ?? null }),
    })).filter((v: any) => v.value_text !== null && v.value_text !== undefined || v.value_number != null || v.value_bool != null);

    saveCFValues.mutate({ job_id: createdJobId, values });
  }

  async function handleSaveFormData() {
    if (!createdJobId) return msgApi.warning("Save job details first.");
    if (!selectedTemplateId) return msgApi.warning("Select a form template.");
    try {
      const vals = await formDataForm.validateFields();
      const field_responses: any[] = [];

      if (templateDetail?.sections) {
        templateDetail.sections.forEach((section: any) => {
          (section.fields || []).forEach((field: any) => {
            const rawVal = vals.form_fields?.[String(field.id)];
            if (rawVal === undefined || rawVal === null) return;
            const entry: any = { field_id: field.id };
            if (field.field_type === "checkbox" || field.field_type === "radio" && typeof rawVal === "boolean") {
              entry.value_bool = rawVal;
            } else if (field.field_type === "number") {
              entry.value_number = rawVal;
            } else if (field.field_type === "date") {
              entry.value_date = dayjs(rawVal).format("YYYY-MM-DD");
            } else {
              entry.value_text = String(rawVal);
            }
            field_responses.push(entry);
          });
        });
      }

      saveFormResp.mutate({
        job_id: createdJobId,
        template_id: selectedTemplateId,
        submitted_by: 1,
        field_responses,
      });
    } catch {}
  }

  // ── Table columns ──────────────────────────────────────────────────────────
  const columns = [
    {
      title: "Job #",
      dataIndex: "job_number",
      key: "job_number",
      render: (v: string, r: any) => (
        <div>
          <div className="font-medium text-slate-800 text-sm">{v}</div>
          <div className="text-xs text-slate-400 truncate max-w-[160px]">{r.title}</div>
        </div>
      ),
    },
    {
      title: "Client / Site",
      key: "client",
      render: (_: any, r: any) => (
        <div>
          <div className="text-sm text-slate-700">{r.client_name || "—"}</div>
          {r.site_name && <div className="text-xs text-slate-400">{r.site_name}</div>}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: string) => <Tag color={STATUS_TAG[v]}>{v?.replace("_", " ").toUpperCase()}</Tag>,
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: (v: string) => <Tag color={PRIORITY_TAG[v]}>{v?.toUpperCase()}</Tag>,
    },
    {
      title: "Scheduled",
      dataIndex: "scheduled_start",
      key: "scheduled_start",
      render: (v: string) => v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—",
    },
    {
      title: "Assigned",
      dataIndex: "assigned_user_name",
      key: "assigned_user_name",
      render: (v: string) => v || "Unassigned",
    },
    {
      title: "Value",
      dataIndex: "estimated_value",
      key: "estimated_value",
      render: (v: number) => v != null ? `£${Number(v).toFixed(2)}` : "—",
    },
    {
      title: "",
      key: "actions",
      render: (_: any, r: any) => (
        <Space>
          <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
          <Popconfirm title="Delete this job?" onConfirm={() => deleteJob.mutate(r.id)} okButtonProps={{ danger: true }}>
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Task helpers ───────────────────────────────────────────────────────────
  const TASK_STATUS_COLOR: Record<string, string> = { pending: "default", in_progress: "blue", done: "green" };
  const TASK_STATUS_OPTS = [
    { value: "pending", label: "Pending" },
    { value: "in_progress", label: "In Progress" },
    { value: "done", label: "Done" },
  ];

  function openAddTask() {
    setEditingTask(null);
    taskForm.resetFields();
    taskForm.setFieldsValue({ status: "pending" });
    setTaskModalOpen(true);
  }
  function openEditTask(t: any) {
    setEditingTask(t);
    taskForm.setFieldsValue({ title: t.title, description: t.description, status: t.status, assigned_user_id: t.assigned_user_id });
    setTaskModalOpen(true);
  }
  async function handleSaveTask() {
    try {
      const vals = await taskForm.validateFields();
      const payload = { ...vals, job_id: createdJobId };
      if (editingTask) updateTask.mutate({ id: editingTask.id, ...vals });
      else createTask.mutate(payload);
    } catch {}
  }

  // ── Tabs ───────────────────────────────────────────────────────────────────
  const tabItems = [
    {
      key: "details",
      label: "Job Details",
      children: (
        <Form form={detailsForm} layout="vertical" size="middle">
          <Form.Item name="title" label="Job Title" rules={[{ required: true, message: "Title required" }]}>
            <Input placeholder="e.g. Annual gas safety inspection" />
          </Form.Item>

          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="status" label="Status">
              <Select defaultValue="draft" options={STATUS_OPTS} />
            </Form.Item>
            <Form.Item name="priority" label="Priority">
              <Select defaultValue="normal" options={PRIORITY_OPTS} />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="client_id" label="Client">
              <Select
                showSearch
                allowClear
                placeholder="Select client"
                optionFilterProp="label"
                options={clients.map((c: any) => ({
                  value: c.id,
                  label: `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email,
                }))}
              />
            </Form.Item>
            <Form.Item name="assigned_user_id" label="Assigned To">
              <Select
                showSearch
                allowClear
                placeholder="Select user"
                optionFilterProp="label"
                options={users.map((u: any) => ({
                  value: u.id,
                  label: `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email,
                }))}
              />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="scheduled_start" label="Scheduled Start">
              <DatePicker showTime className="w-full" format="DD/MM/YYYY HH:mm" />
            </Form.Item>
            <Form.Item name="scheduled_end" label="Scheduled End">
              <DatePicker showTime className="w-full" format="DD/MM/YYYY HH:mm" />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="estimated_value" label="Estimated Value (£)">
              <InputNumber className="w-full" placeholder="0.00" min={0} prefix="£" />
            </Form.Item>
            <Form.Item name="po_number" label="PO Number">
              <Input placeholder="Client PO reference…" />
            </Form.Item>
          </div>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Job description…" />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Internal notes…" />
          </Form.Item>

          <Button
            type="primary"
            loading={createJob.isPending || updateJob.isPending}
            onClick={handleSaveDetails}
            style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
          >
            {modal.job ? "Update Job" : "Create Job"}
          </Button>

          {createdJobId && !modal.job && (
            <span className="ml-3 text-green-600 text-sm">Job created (ID: {createdJobId}) — now fill custom fields or form data.</span>
          )}
        </Form>
      ),
    },
    {
      key: "custom_fields",
      label: `Custom Fields (${cfDefs.length})`,
      children: cfDefs.length === 0 ? (
        <div className="py-8 text-center text-slate-400">
          No custom field definitions for jobs in this company.
        </div>
      ) : (
        <Form form={detailsForm} layout="vertical" size="middle">
          {cfDefs.map((def: any) => {
            const name = `cf_${def.id}`;
            if (def.field_type === "boolean") {
              return (
                <Form.Item key={def.id} name={name} label={def.field_label} valuePropName="checked">
                  <Checkbox />
                </Form.Item>
              );
            }
            if (def.field_type === "select") {
              let opts: any[] = [];
              try { opts = typeof def.options === "string" ? JSON.parse(def.options) : def.options || []; } catch {}
              return (
                <Form.Item key={def.id} name={name} label={def.field_label}>
                  <Select
                    options={opts.map((o: any) => ({ value: o, label: o }))}
                    placeholder={`Select ${def.field_label}`}
                    allowClear
                  />
                </Form.Item>
              );
            }
            if (def.field_type === "number") {
              return (
                <Form.Item key={def.id} name={name} label={def.field_label}>
                  <InputNumber className="w-full" />
                </Form.Item>
              );
            }
            return (
              <Form.Item key={def.id} name={name} label={def.field_label}>
                <Input placeholder={`Enter ${def.field_label}`} />
              </Form.Item>
            );
          })}
          <Button
            type="primary"
            loading={saveCFValues.isPending}
            onClick={handleSaveCF}
            style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
          >
            Save Custom Fields
          </Button>
          {!createdJobId && (
            <span className="ml-3 text-orange-500 text-sm">Create job first in Job Details tab.</span>
          )}
        </Form>
      ),
    },
    {
      key: "form_data",
      label: `Form Data`,
      children: (
        <div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">Select Form Template</label>
            <Select
              placeholder="Choose a form template…"
              className="w-full"
              options={formTemplates.map((t: any) => ({ value: t.id, label: t.name }))}
              value={selectedTemplateId}
              onChange={(v) => { setSelectedTemplateId(v); formDataForm.resetFields(); }}
              allowClear
              onClear={() => setSelectedTemplateId(null)}
            />
          </div>

          {selectedTemplateId && templateDetail ? (
            <Form form={formDataForm} layout="vertical" size="middle">
              {(templateDetail.sections || []).map((section: any) => (
                <div key={section.id} className="mb-5">
                  <Divider orientation="left" plain>
                    <span className="font-semibold text-slate-700 text-sm">{section.title}</span>
                    {section.description && (
                      <span className="ml-2 text-slate-400 text-xs">{section.description}</span>
                    )}
                  </Divider>
                  {(section.fields || []).map((field: any) => renderFormField(field, formDataForm))}
                </div>
              ))}

              <Button
                type="primary"
                loading={saveFormResp.isPending}
                onClick={handleSaveFormData}
                style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
              >
                Save Form Data
              </Button>
              {!createdJobId && (
                <span className="ml-3 text-orange-500 text-sm">Create job first in Job Details tab.</span>
              )}
            </Form>
          ) : formTemplates.length === 0 ? (
            <div className="py-8 text-center text-slate-400">No form templates found. Create one in Job Config first.</div>
          ) : null}
        </div>
      ),
    },
    {
      key: "tasks",
      label: `Tasks (${tasks.length})`,
      children: (
        <div>
          {!createdJobId && <div className="mb-3 text-orange-500 text-sm">Save job details first.</div>}
          <div className="mb-3 flex justify-between items-center">
            <span className="text-sm text-slate-600 font-medium">Job Tasks</span>
            <Button size="small" icon={<Plus size={14} />} onClick={openAddTask} disabled={!createdJobId}
              style={{ background: "#4c1d95", borderColor: "#4c1d95", color: "#fff" }}>
              Add Task
            </Button>
          </div>
          {tasks.length === 0 ? (
            <div className="py-6 text-center text-slate-400 text-sm">No tasks yet.</div>
          ) : (
            <div className="space-y-2">
              {tasks.map((t: any) => (
                <div key={t.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-800 text-sm">{t.title}</span>
                      <Tag color={TASK_STATUS_COLOR[t.status]}>{t.status?.replace("_", " ").toUpperCase()}</Tag>
                    </div>
                    {t.description && <div className="text-xs text-slate-400 mt-0.5">{t.description}</div>}
                    {t.assigned_user_name?.trim() && <div className="text-xs text-slate-400 mt-0.5">Assigned: {t.assigned_user_name}</div>}
                  </div>
                  <Space size="small">
                    <Button size="small" onClick={() => openEditTask(t)}>Edit</Button>
                    {t.status !== "done" && (
                      <Button size="small" type="primary" ghost onClick={() => updateTask.mutate({ id: t.id, status: "done" })}>Done</Button>
                    )}
                    <Popconfirm title="Delete task?" onConfirm={() => deleteTask.mutate({ id: t.id })} okButtonProps={{ danger: true }}>
                      <Button size="small" danger>Del</Button>
                    </Popconfirm>
                  </Space>
                </div>
              ))}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "checklists",
      label: "Checklists",
      children: (
        <div>
          {!createdJobId && <div className="mb-3 text-orange-500 text-sm">Save job details first.</div>}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">Select Checklist Template</label>
            <Select
              placeholder="Choose a checklist template…"
              className="w-full"
              options={checklistTemplates.map((ct: any) => ({ value: ct.id, label: ct.name }))}
              value={selectedChecklistId}
              onChange={(v) => setSelectedChecklistId(v)}
              allowClear
              onClear={() => setSelectedChecklistId(null)}
            />
          </div>
          {selectedChecklistId && (() => {
            const tpl = checklistTemplates.find((ct: any) => ct.id === selectedChecklistId);
            if (!tpl) return null;
            const items: any[] = tpl.items || [];
            return (
              <div className="space-y-3">
                {items.map((item: any) => {
                  const existing = checklistResponses.find(
                    (r: any) => r.template_id === selectedChecklistId && r.item_id === item.id,
                  );
                  return (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
                      <div>
                        <div className="text-sm text-slate-800 font-medium">{item.item_text}</div>
                        <div className="text-xs text-slate-400">{item.response_type}</div>
                      </div>
                      <Space size="small">
                        {item.response_type === "checkbox" || item.response_type === "pass_fail" ? (
                          <>
                            <Button size="small" type={existing?.response_bool === true ? "primary" : "default"}
                              style={existing?.response_bool === true ? { background: "#10b981", borderColor: "#10b981" } : {}}
                              onClick={() => saveChecklistResp.mutate({ job_id: createdJobId, template_id: selectedChecklistId, item_id: item.id, response_bool: true, answered_by: 1 })}>
                              Pass ✓
                            </Button>
                            <Button size="small" danger={existing?.response_bool === false}
                              onClick={() => saveChecklistResp.mutate({ job_id: createdJobId, template_id: selectedChecklistId, item_id: item.id, response_bool: false, answered_by: 1 })}>
                              Fail ✗
                            </Button>
                          </>
                        ) : (
                          <Input
                            size="small"
                            defaultValue={existing?.response ?? ""}
                            placeholder="Enter response…"
                            onBlur={(e) => {
                              if (e.target.value !== (existing?.response ?? "")) {
                                saveChecklistResp.mutate({ job_id: createdJobId, template_id: selectedChecklistId, item_id: item.id, response: e.target.value, answered_by: 1 });
                              }
                            }}
                            style={{ width: 200 }}
                          />
                        )}
                      </Space>
                    </div>
                  );
                })}
                {items.length === 0 && <div className="py-4 text-center text-slate-400 text-sm">No items in this checklist.</div>}
              </div>
            );
          })()}
          {!selectedChecklistId && checklistTemplates.length === 0 && (
            <div className="py-6 text-center text-slate-400 text-sm">No checklist templates. Create one in Job Config first.</div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      {ctx}
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-violet-600 hover:text-violet-800 flex items-center gap-1 text-sm">
            <ArrowLeft size={16} /> Back
          </button>
          <div>
            <h1 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
              <Briefcase size={20} className="text-violet-600" />
              Jobs — Company #{cid}
            </h1>
            <p className="text-slate-500 text-xs">{total} job{total !== 1 ? "s" : ""} total</p>
          </div>
        </div>
        <Button
          type="primary"
          icon={<Plus size={16} />}
          onClick={openCreate}
          style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
          className="flex items-center gap-2"
        >
          New Job
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Input.Search
          placeholder="Search jobs…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
          allowClear
        />
        <Select
          value={statusFilter || undefined}
          onChange={(v) => setStatusFilter(v ?? "")}
          placeholder="All Statuses"
          options={[{ value: "", label: "All Statuses" }, ...STATUS_OPTS]}
          className="w-44"
          allowClear
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <Table
          rowKey="id"
          dataSource={jobs}
          columns={columns}
          loading={isLoading}
          pagination={{
            current: page,
            pageSize: 20,
            total,
            onChange: (p) => setPage(p),
            showTotal: (t) => `${t} jobs`,
          }}
          size="middle"
        />
      </div>

      {/* Create / Edit Modal */}
      <Modal
        open={modal.open}
        onCancel={closeModal}
        title={
          <span className="text-base font-semibold">
            {modal.job ? `Edit — ${modal.job.job_number}` : "Create New Job"}
          </span>
        }
        width={700}
        footer={null}
        destroyOnClose
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className="pt-1"
        />
      </Modal>

      {/* Task Create / Edit Modal */}
      <Modal
        open={taskModalOpen}
        onCancel={() => { setTaskModalOpen(false); taskForm.resetFields(); }}
        title={editingTask ? "Edit Task" : "Add Task"}
        width={440}
        onOk={handleSaveTask}
        okButtonProps={{ style: { background: "#4c1d95", borderColor: "#4c1d95" }, loading: createTask.isPending || updateTask.isPending }}
        okText={editingTask ? "Save" : "Create"}
        destroyOnClose
      >
        <Form form={taskForm} layout="vertical" size="middle" className="pt-2">
          <Form.Item name="title" label="Task Title" rules={[{ required: true, message: "Title required" }]}>
            <Input placeholder="e.g. Check gas meter readings" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Optional details…" />
          </Form.Item>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="status" label="Status">
              <Select options={TASK_STATUS_OPTS} />
            </Form.Item>
            <Form.Item name="assigned_user_id" label="Assigned To">
              <Select showSearch allowClear placeholder="Select user" optionFilterProp="label"
                options={[{ value: null as any, label: "Unassigned" }, ...((users as any[]).map((u: any) => ({ value: u.id, label: `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email })))]} />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
